import React, { useCallback, useRef, useState } from "react";
import { Button, Col, Form, Row, Select, DatePicker, message } from "antd";
import { connect } from "dva";
import { router } from "umi";
import { Integrations, TrailerType } from "@/utils/enum";
import { disableDatesBeforeToday, checkUniqueAddresses } from "@/utils/utils";
import { fetchAssociatedUsers } from "@/services/entityGroups";
import { createDraftShipment } from "@/services/shipments";
import useScreenDetector from "@/hooks/useScreenDetector";
import DealerInput from "@/components/DealerInput";
import NotFoundContent from "@/components/NotFoundContent";
import { flatPhoneNumber } from "@/components/PhoneNumberInput";
import IntegrationInputs from "./components/IntegrationInputs";
import LocationInformationInputs from "./components/LocationInformationInputs";
import SpecialConditionsInputs from "./components/SpecialConditionsInputs";
import initialValues from "./constants/initialValues";
import { parseAddressData } from "./utils";

const { Option } = Select;

const breakPoints = { xs: 24, s: 24, md: 24, lg: 12, xl: 12, xxl: 12 };

const CreateShipmentForm = ({ configPerm, isAdmin }) => {
  const { isMobile } = useScreenDetector();

  const createShipmentFormRef = useRef();
  const wwlIntegrationFormRef = useRef();

  const [submitting, setSubmitting] = useState(false);
  const [isSpecialConditionsOpen, setIsSpecialConditionsOpen] = useState(false);
  const [wwlIntegrationInputsVisible, setWWLIntegrationInputsVisible] = useState(false);
  const [showSelectAvailableDate, setShowSelectAvailableDate] = useState(false);
  const [dealerInputHidden, setDealerInputHidden] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [dealerId, setDealerId] = useState(null);
  const originalSelectedAddresses = useRef({}); // store a copy of the original addresses from the address book to check later if changes were made

  const handleIsSpecialConditionsOpenChange = useCallback(([isOpen]) => {
    setIsSpecialConditionsOpen(isOpen);
  }, []);

  const onAvailableChange = useCallback(value => {
    setShowSelectAvailableDate(!value);
  }, []);

  const updateShipperSelectOptions = useCallback(
    async dealerId => {
      if (!configPerm["CoreService.AssociateUsers"]) {
        return;
      }

      setLoadingUsers(true);

      try {
        const res = await fetchAssociatedUsers(dealerId);
        const items = res?.items || [];
        setConnectedUsers(items);

        if (isAdmin && items.length === 1) {
          createShipmentFormRef.current.setFieldsValue({
            userId: items[0].userId,
          });
        }
      } catch (err) {
        console.log(err);
      }

      setLoadingUsers(false);
    },
    [configPerm, isAdmin],
  );

  const onSelectDealer = useCallback(
    dealer => {
      const { id: dealerId, integrations = [] } = dealer;

      createShipmentFormRef.current.setFieldsValue({
        userId: null,
      });

      updateShipperSelectOptions(dealerId);
      setWWLIntegrationInputsVisible(integrations?.some(integration => integration.type === Integrations.WWL));

      setDealerId(dealerId);
    },
    [updateShipperSelectOptions],
  );

  const onSubmit = useCallback(
    async values => {
      // validate addresses are unique
      const validAddresses = checkUniqueAddresses(values.Pickup, values.Dropoff);

      if (!validAddresses) {
        message.error("Pickup and Dropoff addresses cannot be the same");
        return;
      }

      values.Pickup.contactPhone = flatPhoneNumber(values.Pickup.contactPhone);
      values.Dropoff.contactPhone = flatPhoneNumber(values.Dropoff.contactPhone);

      values.Pickup.dayOfWeekRestricted = values.Pickup.dayOfWeekRestricted.sort().toString();
      values.Dropoff.dayOfWeekRestricted = values.Dropoff.dayOfWeekRestricted.sort().toString();

      /**
       * @type {CreateShipmentRequestData}
       */
      let createShipmentRequestData = {};

      // Availability
      createShipmentRequestData.isAvailable = values.isAvailable;

      if (!values.isAvailable) {
        createShipmentRequestData.availableAt = values.availableAt.toISOString();
      }

      // Trailer Type
      createShipmentRequestData.trailerType = values.trailerType;

      // check if the pickup address is the same as the original one
      const isSamePickupAsOriginal = !checkUniqueAddresses(values.Pickup, originalSelectedAddresses.current.Pickup);

      // If autofilled from address book and the pickup address is the same as the original one, include that addressId in request
      if (values.Pickup.originalAddressId && isSamePickupAsOriginal) {
        createShipmentRequestData.originalPickupAddressId = values.Pickup.originalAddressId;
      }

      createShipmentRequestData.pickupAddress = parseAddressData(values.Pickup);
      createShipmentRequestData.pickupAddress.dealerId = values.dealerId;
      createShipmentRequestData.pickupAddress.notes = values.Pickup.instructionsOrRestrictions;

      // check if the pickup address is the same as the original one
      const isSameDropoffAsOriginal = !checkUniqueAddresses(values.Dropoff, originalSelectedAddresses.current.Dropoff);

      // If autofilled from address book & the pickup address is the same as the original one, include that addressId in request
      if (values.Dropoff.originalAddressId && isSameDropoffAsOriginal) {
        createShipmentRequestData.originalDropOffAddressId = values.Dropoff.originalAddressId;
      }

      createShipmentRequestData.dropOffAddress = parseAddressData(values.Dropoff);

      createShipmentRequestData.dropOffAddress.dealerId = values.dealerId;
      createShipmentRequestData.dropOffAddress.notes = values.Dropoff.instructionsOrRestrictions;

      createShipmentRequestData.dealerId = values.dealerId;

      if (values.userId) {
        createShipmentRequestData.contactShipperId = values.userId;
      }

      // Special conditions
      createShipmentRequestData.requiresTWIC = values.requiresTWIC ?? false;
      createShipmentRequestData.requiresEModal = values.requiresEModal ?? false;
      createShipmentRequestData.requiresLotExit = values.requiresLotExit ?? false;

      // Optional integration inputs
      if (wwlIntegrationInputsVisible) {
        const { vpc, railHead, carrierCode, sequenceNumber } = wwlIntegrationFormRef?.current?.getFieldsValue() ?? {};

        createShipmentRequestData.vpc = vpc;
        createShipmentRequestData.railHead = railHead;
        createShipmentRequestData.carrierCode = carrierCode;
        createShipmentRequestData.sequenceNumber = sequenceNumber;
        createShipmentRequestData.tenderFileUrl = "test";
        createShipmentRequestData.integrationType = Integrations.WWL;
      }

      setSubmitting(true);

      try {
        const response = await createDraftShipment(createShipmentRequestData);

        if (response.error) {
          if (
            response.message === "Can't create shipment with an inactive dealer."
            // Do nothing, the error message will be displayed by the request function
            // otherwise it will display the error message twice
          ) {
            return;
          }

          message.error("Something went wrong while creating the shipment.");
          throw new Error();
        }

        message.success("Successfully created shipment!");

        const { id: shipmentId } = response;

        const url = isAdmin
          ? `/admin/shipments/view?shipmentId=${shipmentId}`
          : `/shipments/dealer/all/view?shipmentId=${shipmentId}`;
        router.push(url);
      } catch (err) {
        message.error("Error creating shipment");
        console.log(err);
      } finally {
        setSubmitting(false);
      }
    },
    [wwlIntegrationInputsVisible, isAdmin],
  );

  const onSubmitError = useCallback(() => {
    message.error("Form is missing required fields");
  }, []);

  const copyOriginalAddress = useCallback((address, type) => (originalSelectedAddresses.current[type] = address), []);

  return (
    <Form
      ref={createShipmentFormRef}
      layout="vertical"
      initialValues={{
        isAvailable: true,
        trailerType: TrailerType.Open,
        Pickup: initialValues,
        Dropoff: initialValues,
        // Special Conditions
        requiresTWIC: false,
        requiresEModal: false,
        requiresLotExit: false,
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
      onFinish={onSubmit}
      onFinishFailed={onSubmitError}
    >
      <Row gutter={16} style={{ width: "100%" }}>
        <Col flex={1}>
          <Row gutter={16} style={{ paddingLeft: 8, paddingRight: 8 }}>
            <DealerInput
              formRef={createShipmentFormRef}
              rules={[{ required: true, message: "Please select a dealer" }]}
              onChange={onSelectDealer}
              onHiddenChange={setDealerInputHidden}
              xs={24}
              xl={8}
            />

            <Col xs={24} xl={dealerInputHidden ? 12 : 8}>
              <Form.Item name="trailerType" label="Trailer Type" rules={[{ required: true }]} style={{ width: "100%" }}>
                <Select getPopupContainer={trigger => trigger.parentElement}>
                  <Option value={TrailerType.Open}>Open</Option>
                  <Option value={TrailerType.Enclosed}>Enclosed</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} xl={showSelectAvailableDate ? (dealerInputHidden ? 6 : 4) : dealerInputHidden ? 12 : 8}>
              <Form.Item
                name="isAvailable"
                label="Available Now"
                // availability not required on form validation for submission to pricing engine
                rules={[{ required: true }]}
              >
                <Select onChange={onAvailableChange} getPopupContainer={trigger => trigger.parentElement}>
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>
            </Col>

            {showSelectAvailableDate && (
              <Col xs={24} xl={showSelectAvailableDate ? (dealerInputHidden ? 6 : 4) : dealerInputHidden ? 12 : 8}>
                <Form.Item
                  name="availableAt"
                  label="Available At"
                  // availability not required on form validation for submission to pricing engine
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    format="MM/DD/YYYY"
                    getPopupContainer={trigger => trigger.parentElement}
                    style={{ width: "100%" }}
                    disabledDate={disableDatesBeforeToday}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {isAdmin && (
            <Col xs={24}>
              <Form.Item name="userId" label="Shipper" rules={[{ required: true, message: "Please select a user" }]}>
                <Select
                  showSearch
                  autoComplete="dontshow"
                  placeholder="Select a user"
                  notFoundContent={NotFoundContent({
                    loading: loadingUsers,
                  })}
                  filterOption={(input, option) => option.key.toUpperCase().indexOf(input.toUpperCase()) !== -1}
                >
                  {connectedUsers.map(user => {
                    return (
                      <Select.Option
                        disabled={false}
                        key={`${user.userId || ""}${user.userName || ""}${user.email || ""}`}
                        value={user.userId}
                      >
                        {user.userName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Row gutter={16}>
            <Col {...breakPoints} style={{ marginBottom: "20px" }}>
              <LocationInformationInputs
                canSaveAddress={!!configPerm["CoreService.Addresses.Create"]}
                locationType="Pickup"
                formRef={createShipmentFormRef}
                dealerId={dealerId}
                isAdmin={isAdmin}
                onSelectAddress={address => copyOriginalAddress(address, "Pickup")}
              />
            </Col>
            <Col {...breakPoints} style={{ marginBottom: "20px" }}>
              <LocationInformationInputs
                canSaveAddress={!!configPerm["CoreService.Addresses.Create"]}
                locationType="Dropoff"
                formRef={createShipmentFormRef}
                dealerId={dealerId}
                isAdmin={isAdmin}
                onSelectAddress={address => copyOriginalAddress(address, "Dropoff")}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {isAdmin && wwlIntegrationInputsVisible && (
        <div style={{ width: "100%" }}>
          <IntegrationInputs wwlIntegrationFormRef={wwlIntegrationFormRef} />
        </div>
      )}

      {isAdmin && (
        <div style={{ width: "100%" }}>
          <SpecialConditionsInputs
            isOpen={isSpecialConditionsOpen}
            onIsOpenChange={handleIsSpecialConditionsOpenChange}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: isMobile ? "center" : "flex-end",
          marginTop: 16,
        }}
      >
        <Form.Item style={isMobile ? { width: "90%", marginBottom: 0 } : {}}>
          <Button type="primary" htmlType="submit" block={isMobile} loading={submitting}>
            Next
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(CreateShipmentForm);
